import { Magnitude } from './Magnitude';
import {
  ESTATE_TIERS,
  LEGACY_UPGRADES,
  MANAGER_COST_GROWTH,
  MANAGER_EFFICIENCIES,
  MAX_MANAGER_LEVEL,
  MILESTONES,
  PHILOSOPHIES,
  REGIONS,
  SALES_CHANNELS,
  STATIONS,
  STATION_ORDER,
  WINES,
  WINE_ORDER
} from './content';
import type {
  BottleneckRecommendation,
  GameCommand,
  GameSettings,
  GameState,
  InventoryStage,
  LegacyUpgradeState,
  OfflineSummary,
  OwnerProfile,
  PipelineRecommendationAction,
  PipelineStationStatus,
  RegionId,
  SalesChannelId,
  StationId,
  StationStatusKind,
  WineId,
  WineInventory,
  WineryStats
} from './types';

export const GAME_VERSION = '1.0.0';
export const ECONOMY_TICK = 0.25;
const ACTIVE_BOOST_SECONDS = 45;
const STATUS_ENTER_SECONDS = 2;
const STATUS_CLEAR_SECONDS = 0.75;
const INVENTORY_EPSILON = 0.00001;

export const DEFAULT_SETTINGS: GameSettings = {
  reducedMotion: false,
  highContrast: false,
  numberFormat: 'suffix',
  temperatureUnit: 'celsius',
  musicVolume: 0.35,
  effectsVolume: 0.7,
  ambienceVolume: 0.45
};

const emptyStats = (): WineryStats => ({
  revenue: Magnitude.zero(),
  profit: Magnitude.zero(),
  bottlesProduced: 0,
  customersServed: 0,
  manualActions: 0,
  bestQuality: 0,
  upgradesPurchased: 0
});

const emptyLegacy = (): LegacyUpgradeState => ({
  inheritance: 0,
  oldVines: 0,
  cellarLore: 0,
  nightShift: 0,
  tradeContacts: 0,
  blueprints: 0
});

const defaultAllocations = (): Record<SalesChannelId, number> => ({
  tasting: 70,
  club: 0,
  restaurant: 0,
  distributor: 0,
  bulk: 30
});

export function createInitialState(
  region: RegionId,
  owner: OwnerProfile,
  retained?: Pick<GameState, 'legacyCorks' | 'legacyUpgrades' | 'bottleHistory' | 'settings' | 'renown' | 'estateTier' | 'lifetimeStats' | 'vintage' | 'year'>
): GameState {
  const legacyUpgrades = retained?.legacyUpgrades ?? emptyLegacy();
  const renown = retained?.renown ?? 0;
  const estateTier = retained?.estateTier ?? 1;
  const startCash = 320 + legacyUpgrades.inheritance * 750 + Math.max(0, (retained?.vintage ?? 1) - 1) * 80;
  const qualityBase = 63 + REGIONS[region].qualityBonus + legacyUpgrades.cellarLore * 1.5;
  const stations = Object.fromEntries(STATION_ORDER.map((id) => [id, {
    level: 1,
    capacityLevel: 0,
    managerLevel: 0,
    automated: false,
    boostUntil: 0,
    qualityBoostUntil: 0,
    produced: 0,
    status: 'manual',
    statusSince: 0,
    pendingStatus: null,
    pendingStatusSince: 0,
    blocked: false
  }])) as GameState['stations'];
  const inventory = Object.fromEntries(WINE_ORDER.map((id) => [id, {
    grapes: 0,
    juice: 0,
    wine: 0,
    cases: 0
  }])) as Record<WineId, WineInventory>;
  const wines = Object.fromEntries(WINE_ORDER.map((id) => {
    const def = WINES[id];
    const unlocked = def.unlockRenown <= renown && def.unlockTier <= estateTier;
    return [id, {
      unlocked,
      priority: id === 'sauvignon' ? 'high' : 'normal',
      price: def.baseValue,
      allocations: defaultAllocations(),
      quality: Math.min(96, qualityBase + (id === 'reserve' ? 3 : 0)),
      traits: [...def.preferredTraits],
      soldCases: 0,
      nextBottleAt: 20
    }];
  })) as GameState['wines'];

  wines.sauvignon.unlocked = true;

  return {
    schemaVersion: 1,
    gameVersion: GAME_VERSION,
    region,
    owner,
    philosophy: 'boutique',
    cash: Magnitude.from(startCash),
    renown,
    legacyCorks: retained?.legacyCorks ?? 0,
    vintage: retained?.vintage ?? 1,
    year: retained?.year ?? 2026,
    estateTier,
    elapsedSeconds: 0,
    lastSavedAt: Date.now(),
    weather: 'Golden & clear',
    weatherTemperature: region === 'napa' ? 27 : 24,
    stations,
    wines,
    inventory,
    legacyUpgrades,
    bottleHistory: retained?.bottleHistory ?? [],
    stats: emptyStats(),
    lifetimeStats: retained?.lifetimeStats ?? emptyStats(),
    settings: { ...DEFAULT_SETTINGS, ...retained?.settings },
    tutorialStep: 0
  };
}

export function cloneState(source: GameState): GameState {
  return {
    ...source,
    cash: Magnitude.from(source.cash),
    stations: Object.fromEntries(STATION_ORDER.map((id) => [id, { ...source.stations[id] }])) as GameState['stations'],
    wines: Object.fromEntries(WINE_ORDER.map((id) => [id, {
      ...source.wines[id],
      allocations: { ...source.wines[id].allocations },
      traits: [...source.wines[id].traits]
    }])) as GameState['wines'],
    inventory: Object.fromEntries(WINE_ORDER.map((id) => [id, { ...source.inventory[id] }])) as GameState['inventory'],
    legacyUpgrades: { ...source.legacyUpgrades },
    bottleHistory: source.bottleHistory.map((record) => ({ ...record, traits: [...record.traits] })),
    stats: { ...source.stats, revenue: Magnitude.from(source.stats.revenue), profit: Magnitude.from(source.stats.profit) },
    lifetimeStats: { ...source.lifetimeStats, revenue: Magnitude.from(source.lifetimeStats.revenue), profit: Magnitude.from(source.lifetimeStats.profit) },
    settings: { ...source.settings }
  };
}

export function milestoneMultiplier(level: number): number {
  let multiplier = 1;
  if (level >= 10) multiplier *= 2;
  if (level >= 25) multiplier *= 3;
  if (level >= 50) multiplier *= 5;
  if (level >= 100) multiplier *= 10;
  if (level >= 200) multiplier *= 4;
  return multiplier;
}

export function stationRate(state: GameState, stationId: StationId): number {
  const station = state.stations[stationId];
  const def = STATIONS[stationId];
  const philosophy = PHILOSOPHIES[state.philosophy];
  let rate = def.baseRate * station.level * 1.105 ** Math.max(0, station.level - 1) * milestoneMultiplier(station.level);
  rate *= philosophy.output;
  if (station.boostUntil > state.elapsedSeconds) rate *= 2;
  if (stationId === 'vineyard') rate *= 1 + state.legacyUpgrades.oldVines * 0.18;
  return rate;
}

export function stationCapacity(state: GameState, stationId: StationId): number {
  const station = state.stations[stationId];
  return STATIONS[stationId].baseCapacity * (1 + station.capacityLevel * 0.55) * Math.max(1, Math.sqrt(milestoneMultiplier(station.level)));
}

function costDiscount(state: GameState): number {
  return Math.max(0.35, 1 - state.legacyUpgrades.blueprints * 0.04);
}

export function levelCost(state: GameState, stationId: StationId, level = state.stations[stationId].level): Magnitude {
  const def = STATIONS[stationId];
  const cost = def.baseCost * REGIONS[state.region].costMultiplier * costDiscount(state) * 1.155 ** Math.max(0, level - 1);
  return Magnitude.from(cost);
}

export function managerEfficiency(state: GameState, stationId: StationId): number {
  const level = Math.max(0, Math.min(MAX_MANAGER_LEVEL, Math.floor(state.stations[stationId].managerLevel)));
  return MANAGER_EFFICIENCIES[level];
}

export function effectiveStationRate(state: GameState, stationId: StationId): number {
  return stationRate(state, stationId) * managerEfficiency(state, stationId);
}

export function managerCost(state: GameState, stationId: StationId): Magnitude {
  const station = state.stations[stationId];
  const shortfall = station.managerLevel === 0 ? Math.max(0, 5 - station.level) : 0;
  return Magnitude.from(
    STATIONS[stationId].managerCost
      * MANAGER_COST_GROWTH ** station.managerLevel
      * (1 + shortfall * 0.18)
      * REGIONS[state.region].costMultiplier
      * costDiscount(state)
  );
}

export function capacityCost(state: GameState, stationId: StationId): Magnitude {
  const station = state.stations[stationId];
  return Magnitude.from(STATIONS[stationId].capacityCost * 1.72 ** station.capacityLevel * REGIONS[state.region].costMultiplier * costDiscount(state));
}

export function purchaseQuote(state: GameState, stationId: StationId, request: number | 'max' | 'milestone'): { levels: number; cost: Magnitude } {
  const current = state.stations[stationId].level;
  let targetLevels: number;
  if (request === 'milestone') {
    targetLevels = Math.max(1, (MILESTONES.find((milestone) => milestone > current) ?? current + 25) - current);
  } else if (request === 'max') {
    targetLevels = 0;
    let available = state.cash;
    while (targetLevels < 1000) {
      const cost = levelCost(state, stationId, current + targetLevels);
      if (!available.gte(cost)) break;
      available = available.subtract(cost);
      targetLevels += 1;
    }
  } else {
    targetLevels = Math.max(1, request);
  }
  let cost = Magnitude.zero();
  for (let index = 0; index < targetLevels; index += 1) cost = cost.add(levelCost(state, stationId, current + index));
  return { levels: targetLevels, cost };
}

function stageTotal(state: GameState, stage: InventoryStage): number {
  return WINE_ORDER.reduce((total, wineId) => total + state.inventory[wineId][stage], 0);
}

const inventoryLabel = (stage: InventoryStage): string => ({
  grapes: 'grapes',
  juice: 'juice',
  wine: 'wine',
  cases: 'cases'
})[stage];

const storageLabel = (stage: InventoryStage): string => ({
  grapes: 'Grape',
  juice: 'Juice',
  wine: 'Wine',
  cases: 'Case'
})[stage];

function stationImprovementAction(state: GameState, stationId: StationId): PipelineRecommendationAction {
  const managerLevel = state.stations[stationId].managerLevel;
  if (managerLevel === 0) return 'hire-manager';
  if (managerLevel < MAX_MANAGER_LEVEL) return 'upgrade-manager';
  return 'upgrade-throughput';
}

function rawStationStatus(state: GameState, stationId: StationId, processed: number): StationStatusKind {
  const definition = STATIONS[stationId];
  const fallback = state.stations[stationId].managerLevel > 0 ? 'running' : 'manual';
  if (processed > INVENTORY_EPSILON) return fallback;
  if (definition.output !== 'cash') {
    const capacity = stationCapacity(state, stationId);
    const remaining = capacity - stageTotal(state, definition.output);
    if (remaining <= Math.max(INVENTORY_EPSILON, capacity * 0.002)) return 'blocked';
  }
  if (definition.input && stageTotal(state, definition.input) <= INVENTORY_EPSILON) return 'starved';
  return fallback;
}

function settleStationStatus(
  state: GameState,
  stationId: StationId,
  observed: StationStatusKind,
  deltaSeconds: number
): void {
  const station = state.stations[stationId];
  station.automated = station.managerLevel > 0;
  if (observed === station.status) {
    station.pendingStatus = null;
    station.pendingStatusSince = state.elapsedSeconds;
    station.blocked = station.status === 'blocked';
    return;
  }
  if (station.pendingStatus !== observed) {
    station.pendingStatus = observed;
    station.pendingStatusSince = Math.max(0, state.elapsedSeconds - deltaSeconds);
  }
  const delay = observed === 'blocked' || observed === 'starved'
    ? STATUS_ENTER_SECONDS
    : STATUS_CLEAR_SECONDS;
  if (state.elapsedSeconds - station.pendingStatusSince >= delay) {
    station.status = observed;
    station.statusSince = state.elapsedSeconds;
    station.pendingStatus = null;
    station.pendingStatusSince = state.elapsedSeconds;
  }
  station.blocked = station.status === 'blocked';
}

export function pipelineStatus(state: GameState): PipelineStationStatus[] {
  return STATION_ORDER.map((stationId, index) => {
    const station = state.stations[stationId];
    const definition = STATIONS[stationId];
    const previousId = STATION_ORDER[index - 1] ?? null;
    const nextId = STATION_ORDER[index + 1] ?? null;

    if (station.status === 'blocked' && definition.output !== 'cash') {
      const downstreamIsManual = nextId ? state.stations[nextId].managerLevel === 0 : false;
      const downstreamIsSlower = nextId
        ? effectiveStationRate(state, nextId) < effectiveStationRate(state, stationId) * 0.95
        : false;
      const recommendedStationId = nextId && (downstreamIsManual || downstreamIsSlower) ? nextId : stationId;
      const action: PipelineRecommendationAction = recommendedStationId === stationId
        ? 'expand-storage'
        : downstreamIsManual
          ? 'operate'
          : stationImprovementAction(state, recommendedStationId);
      const nextInstruction = recommendedStationId === stationId
        ? `Expand ${definition.label} storage for more room.`
        : `${downstreamIsManual ? 'Operate' : 'Improve'} ${STATIONS[recommendedStationId].label} so it clears ${inventoryLabel(definition.output)} faster.`;
      return {
        stationId,
        kind: station.status,
        label: `${storageLabel(definition.output)} storage full`,
        explanation: `${definition.label} has paused safely because its output storage stayed full. ${nextInstruction}`,
        recommendedStationId,
        action
      };
    }

    if (station.status === 'starved' && definition.input) {
      const recommendedStationId = previousId ?? stationId;
      const upstreamIsManual = state.stations[recommendedStationId].managerLevel === 0;
      return {
        stationId,
        kind: station.status,
        label: `Waiting for ${inventoryLabel(definition.input)}`,
        explanation: `${definition.label} is ready but has no ${inventoryLabel(definition.input)}. ${upstreamIsManual ? 'Operate' : 'Improve'} ${STATIONS[recommendedStationId].label} to feed it.`,
        recommendedStationId,
        action: upstreamIsManual ? 'operate' : stationImprovementAction(state, recommendedStationId)
      };
    }

    if (station.status === 'manual') {
      return {
        stationId,
        kind: station.status,
        label: 'Owner operated',
        explanation: `${definition.label} moves only when you use “${definition.action}.” Hire a manager when you want steady automatic flow.`,
        recommendedStationId: stationId,
        action: 'operate'
      };
    }

    return {
      stationId,
      kind: station.status,
      label: 'Flowing',
      explanation: `${definition.label} is running automatically at ${Math.round(managerEfficiency(state, stationId) * 100)}% of its station rate.`,
      recommendedStationId: null,
      action: null
    };
  });
}

export function bottleneckRecommendation(state: GameState): BottleneckRecommendation | null {
  const reports = pipelineStatus(state);
  const blocked = reports.find((report) => report.kind === 'blocked' && report.action && report.recommendedStationId);
  if (blocked?.action && blocked.recommendedStationId) {
    return {
      stationId: blocked.recommendedStationId,
      action: blocked.action,
      label: `Clear ${STATIONS[blocked.stationId].label} storage`,
      explanation: blocked.explanation
    };
  }

  const manual = reports.find((report) => report.kind === 'manual');
  if (manual) {
    const canHire = state.cash.gte(managerCost(state, manual.stationId));
    return {
      stationId: manual.stationId,
      action: canHire ? 'hire-manager' : 'operate',
      label: canHire ? `Automate ${STATIONS[manual.stationId].label}` : `Operate ${STATIONS[manual.stationId].label}`,
      explanation: canHire
        ? `${STATIONS[manual.stationId].label} currently stops without you. Hire a level 1 manager for slow, steady flow; later manager levels increase its speed.`
        : `${STATIONS[manual.stationId].label} currently stops without you. Use “${STATIONS[manual.stationId].action}” now; a level 1 manager can automate it once you can afford one.`
    };
  }

  const starved = reports.find((report) => report.kind === 'starved' && report.action && report.recommendedStationId);
  if (starved?.action && starved.recommendedStationId) {
    return {
      stationId: starved.recommendedStationId,
      action: starved.action,
      label: `Feed ${STATIONS[starved.stationId].label}`,
      explanation: starved.explanation
    };
  }

  const backedUp = STATION_ORDER.slice(1)
    .map((stationId, index) => {
      const input = STATIONS[stationId].input as InventoryStage;
      const upstreamId = STATION_ORDER[index];
      return { stationId, fill: stageTotal(state, input) / Math.max(1, stationCapacity(state, upstreamId)) };
    })
    .filter(({ fill }) => fill >= 0.65)
    .sort((a, b) => b.fill - a.fill)[0];
  if (backedUp) {
    return {
      stationId: backedUp.stationId,
      action: stationImprovementAction(state, backedUp.stationId),
      label: `Speed up ${STATIONS[backedUp.stationId].label}`,
      explanation: `${STATIONS[backedUp.stationId].label} has the largest sustained queue in front of it. Improve its manager or station level to move that inventory.`
    };
  }

  return null;
}

function eligibleWines(state: GameState, input: InventoryStage | null): WineId[] {
  const ranks = { high: 0, normal: 1, low: 2 };
  return WINE_ORDER
    .filter((wineId) => state.wines[wineId].unlocked && (input === null || state.inventory[wineId][input] > 0.00001))
    .sort((a, b) => ranks[state.wines[a].priority] - ranks[state.wines[b].priority] || WINE_ORDER.indexOf(a) - WINE_ORDER.indexOf(b));
}

function priorityWeight(state: GameState, wineId: WineId): number {
  return state.wines[wineId].priority === 'high' ? 5 : state.wines[wineId].priority === 'normal' ? 2 : 0.55;
}

function weightedChannelValue(state: GameState, wineId: WineId): { multiplier: number; demand: number; renown: number } {
  const allocations = state.wines[wineId].allocations;
  const available = (Object.keys(allocations) as SalesChannelId[]).filter((id) => state.renown >= SALES_CHANNELS[id].unlockRenown);
  const total = Math.max(1, available.reduce((sum, id) => sum + allocations[id], 0));
  let multiplier = 0;
  let demand = 0;
  let renown = 0;
  for (const id of available) {
    const share = allocations[id] / total;
    multiplier += SALES_CHANNELS[id].multiplier * share;
    demand += SALES_CHANNELS[id].demand * share;
    if (id === 'restaurant') renown += 0.04 * share;
    if (id === 'tasting') renown += 0.018 * share;
  }
  return { multiplier: multiplier || SALES_CHANNELS.bulk.multiplier, demand: demand || 1, renown };
}

export function demandFactor(state: GameState, wineId: WineId): number {
  const wine = state.wines[wineId];
  const def = WINES[wineId];
  const channels = weightedChannelValue(state, wineId);
  const relativePrice = Math.max(0.15, wine.price / def.baseValue);
  const priceElasticity = 1 / relativePrice ** 1.18;
  const quality = 0.58 + wine.quality / 100 * 0.72;
  const reputation = 1 + Math.log10(1 + state.renown) * 0.12;
  return Math.max(0.08, channels.demand * priceElasticity * quality * reputation);
}

function processSales(state: GameState, requested: number, manual: boolean): number {
  const candidates = eligibleWines(state, 'cases');
  if (candidates.length === 0) return 0;
  const totalWeight = candidates.reduce((sum, id) => sum + priorityWeight(state, id), 0);
  let soldTotal = 0;
  for (const wineId of candidates) {
    const weight = priorityWeight(state, wineId) / totalWeight;
    const demand = demandFactor(state, wineId);
    const target = requested * weight * demand * (manual ? 1.25 : 1);
    const sold = Math.min(state.inventory[wineId].cases, target);
    if (sold <= 0) continue;
    state.inventory[wineId].cases -= sold;
    soldTotal += sold;
    const wineState = state.wines[wineId];
    const channels = weightedChannelValue(state, wineId);
    const qualityMultiplier = 0.62 + wineState.quality / 100 * 0.86;
    const regionWineBonus = REGIONS[state.region].wineBonuses[wineId] ?? 1;
    const reputation = 1 + Math.log10(1 + state.renown) * 0.08;
    const legacy = 1 + state.legacyUpgrades.tradeContacts * 0.15;
    const value = sold * wineState.price * channels.multiplier * qualityMultiplier * REGIONS[state.region].salesMultiplier * regionWineBonus * reputation * legacy * PHILOSOPHIES[state.philosophy].sales;
    state.cash = state.cash.add(value);
    state.stats.revenue = state.stats.revenue.add(value);
    state.stats.profit = state.stats.profit.add(value);
    state.lifetimeStats.revenue = state.lifetimeStats.revenue.add(value);
    state.lifetimeStats.profit = state.lifetimeStats.profit.add(value);
    state.stats.customersServed += sold * (manual ? 5 : 3.5);
    state.lifetimeStats.customersServed += sold * (manual ? 5 : 3.5);
    state.renown += sold * (0.024 + channels.renown) * (wineState.quality / 70);
    wineState.soldCases += sold;
    maybeArchiveBottle(state, wineId);
  }
  return soldTotal;
}

function processStation(state: GameState, stationId: StationId, requested: number, manual = false): number {
  const def = STATIONS[stationId];
  const station = state.stations[stationId];
  if (stationId === 'sales') {
    const sold = processSales(state, requested, manual);
    station.produced += sold;
    return sold;
  }
  const output = def.output as InventoryStage;
  const remainingCapacity = Math.max(0, stationCapacity(state, stationId) - stageTotal(state, output));
  if (remainingCapacity <= INVENTORY_EPSILON) return 0;
  const candidates = eligibleWines(state, def.input);
  if (candidates.length === 0) return 0;
  const totalWeight = candidates.reduce((sum, wineId) => sum + priorityWeight(state, wineId), 0);
  let producedTotal = 0;
  let capacityLeft = remainingCapacity;
  for (const wineId of candidates) {
    const difficulty = WINES[wineId].processDifficulty;
    const share = requested * (priorityWeight(state, wineId) / totalWeight) / difficulty;
    const inputAvailable = def.input === null ? Number.POSITIVE_INFINITY : state.inventory[wineId][def.input];
    const produced = Math.min(share, inputAvailable, capacityLeft);
    if (produced <= 0) continue;
    if (def.input !== null) state.inventory[wineId][def.input] -= produced;
    state.inventory[wineId][output] += produced;
    producedTotal += produced;
    capacityLeft -= produced;
    if (stationId === 'cellar') {
      const qualityLift = (manual ? 0.022 : 0.0025) * produced;
      state.wines[wineId].quality = Math.min(100, state.wines[wineId].quality + qualityLift);
      state.stats.bestQuality = Math.max(state.stats.bestQuality, state.wines[wineId].quality);
      state.lifetimeStats.bestQuality = Math.max(state.lifetimeStats.bestQuality, state.wines[wineId].quality);
    }
    if (stationId === 'bottling') {
      state.stats.bottlesProduced += produced * 12;
      state.lifetimeStats.bottlesProduced += produced * 12;
    }
    if (capacityLeft <= 0.00001) break;
  }
  station.produced += producedTotal;
  return producedTotal;
}

function maybeArchiveBottle(state: GameState, wineId: WineId): void {
  const wine = state.wines[wineId];
  if (wine.soldCases < wine.nextBottleAt || wine.quality < 66) return;
  const sequence = Math.floor(wine.nextBottleAt / 20);
  state.bottleHistory.unshift({
    id: `${state.vintage}-${wineId}-${sequence}`,
    wineId,
    vintage: state.vintage,
    year: state.year,
    quality: Math.round(wine.quality * 10) / 10,
    traits: [...wine.traits],
    bottles: Math.round(wine.nextBottleAt * 12),
    note: `Estate archive from the ${state.year} vintage.`,
    value: Math.round(wine.price * (0.7 + wine.quality / 100))
  });
  state.bottleHistory = state.bottleHistory.slice(0, 80);
  wine.nextBottleAt *= 2;
}

function updateUnlocksAndEstate(state: GameState): void {
  const unlockedTier = [...ESTATE_TIERS].reverse().find((tier) => state.renown >= tier.renown)?.tier ?? 1;
  state.estateTier = Math.max(state.estateTier, unlockedTier);
  for (const wineId of WINE_ORDER) {
    const def = WINES[wineId];
    if (state.renown >= def.unlockRenown && state.estateTier >= def.unlockTier) state.wines[wineId].unlocked = true;
  }
}

function updateWeather(state: GameState): void {
  const phase = Math.floor((state.elapsedSeconds / 55) % 4);
  const weather = [
    ['Golden & clear', state.region === 'napa' ? 28 : 24],
    ['Coastal breeze', state.region === 'napa' ? 25 : 21],
    ['Warm afternoon', state.region === 'napa' ? 30 : 26],
    ['Cool evening', state.region === 'napa' ? 20 : 18]
  ] as const;
  [state.weather, state.weatherTemperature] = weather[phase];
}

export function prestigeGain(state: GameState): number {
  const minimumVintageAge = Math.max(600, 1800 / (1 + Math.log2(1 + state.legacyCorks) * 0.16));
  if (state.elapsedSeconds < minimumVintageAge) return 0;
  const revenueScore = Math.log10(1 + Math.max(0, state.stats.revenue.toNumber()) / 25_000) * 3;
  const renownScore = Math.sqrt(Math.max(0, state.renown) / 240);
  return Math.floor(revenueScore + renownScore + Math.max(0, state.estateTier - 2) * 0.35);
}

export function legacyCost(state: GameState, upgradeId: keyof LegacyUpgradeState): number {
  const definition = LEGACY_UPGRADES[upgradeId];
  return Math.ceil(definition.baseCost * 1.72 ** state.legacyUpgrades[upgradeId]);
}

export function offlineCapSeconds(state: GameState): number {
  return [8 * 3600, 24 * 3600, 72 * 3600, Number.POSITIVE_INFINITY][state.legacyUpgrades.nightShift] ?? 8 * 3600;
}

function advanceTickMutable(state: GameState, deltaSeconds: number): void {
  state.elapsedSeconds += deltaSeconds;
  for (const stationId of STATION_ORDER) {
    const station = state.stations[stationId];
    const processed = station.managerLevel > 0
      ? processStation(state, stationId, effectiveStationRate(state, stationId) * deltaSeconds)
      : 0;
    settleStationStatus(state, stationId, rawStationStatus(state, stationId, processed), deltaSeconds);
  }
  updateUnlocksAndEstate(state);
  updateWeather(state);
}

export function simulateElapsed(source: GameState, seconds: number): GameState {
  const state = cloneState(source);
  const bounded = Math.max(0, seconds);
  const step = bounded > 14_400 ? 1 : ECONOMY_TICK;
  let remaining = bounded;
  while (remaining > 0.000001) {
    const delta = Math.min(step, remaining);
    advanceTickMutable(state, delta);
    remaining -= delta;
  }
  return state;
}

export function applyOfflineProgress(source: GameState, now = Date.now()): { state: GameState; summary: OfflineSummary } {
  const rawSeconds = Math.max(0, (now - source.lastSavedAt) / 1000);
  const cap = offlineCapSeconds(source);
  const seconds = Math.min(rawSeconds, cap);
  const beforeCash = source.cash;
  const beforeGrapes = stageTotal(source, 'grapes');
  const beforeCasesSold = WINE_ORDER.reduce((sum, id) => sum + source.wines[id].soldCases, 0);
  const state = simulateElapsed(source, seconds);
  state.lastSavedAt = now;
  return {
    state,
    summary: {
      seconds,
      cashEarned: state.cash.subtract(beforeCash),
      grapesHarvested: Math.max(0, stageTotal(state, 'grapes') - beforeGrapes),
      casesSold: Math.max(0, WINE_ORDER.reduce((sum, id) => sum + state.wines[id].soldCases, 0) - beforeCasesSold),
      capped: rawSeconds > cap
    }
  };
}

export function applyCommand(source: GameState, command: GameCommand): GameState {
  if (command.type === 'tick') return simulateElapsed(source, command.deltaSeconds);
  let state = cloneState(source);
  switch (command.type) {
    case 'manualAction': {
      const station = state.stations[command.stationId];
      const produced = processStation(state, command.stationId, STATIONS[command.stationId].manualAmount * (1 + station.level * 0.035), true);
      if (produced > 0) {
        station.boostUntil = Math.max(station.boostUntil, state.elapsedSeconds + ACTIVE_BOOST_SECONDS);
        if (command.stationId === 'cellar') station.qualityBoostUntil = state.elapsedSeconds + ACTIVE_BOOST_SECONDS;
        state.stats.manualActions += 1;
        state.lifetimeStats.manualActions += 1;
        const index = STATION_ORDER.indexOf(command.stationId);
        if (index === state.tutorialStep) state.tutorialStep = Math.min(6, state.tutorialStep + 1);
      }
      break;
    }
    case 'buyLevels': {
      const quote = purchaseQuote(state, command.stationId, command.amount);
      if (quote.levels > 0 && state.cash.gte(quote.cost)) {
        state.cash = state.cash.subtract(quote.cost);
        state.stations[command.stationId].level += quote.levels;
        state.stats.upgradesPurchased += quote.levels;
        state.lifetimeStats.upgradesPurchased += quote.levels;
        state.tutorialStep = Math.max(state.tutorialStep, 6);
      }
      break;
    }
    case 'buyCapacity': {
      const cost = capacityCost(state, command.stationId);
      if (state.cash.gte(cost)) {
        state.cash = state.cash.subtract(cost);
        state.stations[command.stationId].capacityLevel += 1;
      }
      break;
    }
    case 'upgradeManager': {
      const station = state.stations[command.stationId];
      const cost = managerCost(state, command.stationId);
      if (station.managerLevel < MAX_MANAGER_LEVEL && state.cash.gte(cost)) {
        state.cash = state.cash.subtract(cost);
        station.managerLevel += 1;
        station.automated = true;
        station.pendingStatus = null;
        station.pendingStatusSince = state.elapsedSeconds;
      }
      break;
    }
    case 'setPriority':
      if (state.wines[command.wineId].unlocked) state.wines[command.wineId].priority = command.priority;
      break;
    case 'setPrice':
      state.wines[command.wineId].price = Math.max(1, Math.round(command.price));
      break;
    case 'setAllocation': {
      const allocations = state.wines[command.wineId].allocations;
      const nextValue = Math.max(0, Math.min(100, Math.round(command.value)));
      const otherIds = (Object.keys(allocations) as SalesChannelId[]).filter((id) => id !== command.channelId);
      const otherTotal = otherIds.reduce((sum, id) => sum + allocations[id], 0);
      allocations[command.channelId] = nextValue;
      const remainder = 100 - nextValue;
      if (otherTotal === 0) {
        otherIds.forEach((id, index) => { allocations[id] = index === 0 ? remainder : 0; });
      } else {
        let assigned = 0;
        otherIds.forEach((id, index) => {
          const value = index === otherIds.length - 1 ? remainder - assigned : Math.round(remainder * allocations[id] / otherTotal);
          allocations[id] = Math.max(0, value);
          assigned += allocations[id];
        });
      }
      break;
    }
    case 'setPhilosophy':
      state.philosophy = command.philosophy;
      break;
    case 'buyLegacy': {
      const definition = LEGACY_UPGRADES[command.upgradeId];
      const cost = legacyCost(state, command.upgradeId);
      if (state.legacyUpgrades[command.upgradeId] < definition.max && state.legacyCorks >= cost) {
        state.legacyCorks -= cost;
        state.legacyUpgrades[command.upgradeId] += 1;
      }
      break;
    }
    case 'startVintage': {
      const gain = prestigeGain(state);
      if (gain <= 0) break;
      const retained = {
        legacyCorks: state.legacyCorks + gain,
        legacyUpgrades: { ...state.legacyUpgrades },
        bottleHistory: [...state.bottleHistory],
        settings: { ...state.settings },
        renown: state.renown,
        estateTier: state.estateTier,
        lifetimeStats: { ...state.lifetimeStats },
        vintage: state.vintage + 1,
        year: state.year + 1
      };
      state = createInitialState(state.region, state.owner, retained);
      break;
    }
    case 'setSettings':
      state.settings = { ...state.settings, ...command.settings };
      break;
  }
  updateUnlocksAndEstate(state);
  return state;
}

export function serializeState(state: GameState): string {
  return JSON.stringify(state);
}

export function hydrateState(value: unknown): GameState {
  if (!value || typeof value !== 'object') throw new Error('Save data is not an object.');
  const candidate = value as Partial<GameState> & { cash?: unknown };
  if (candidate.schemaVersion !== 1 || !candidate.region || !candidate.owner || !candidate.stations || !candidate.wines || !candidate.inventory) {
    throw new Error('Save data is missing required fields or uses an unsupported schema.');
  }
  const state = candidate as GameState;
  const hydrateStats = (stats: WineryStats | undefined): WineryStats => {
    const fallback = emptyStats();
    const source = stats ?? fallback;
    return {
      ...fallback,
      ...source,
      revenue: Magnitude.from(source.revenue as unknown as string),
      profit: Magnitude.from(source.profit as unknown as string)
    };
  };
  const isStatus = (status: unknown): status is StationStatusKind => (
    status === 'manual' || status === 'running' || status === 'starved' || status === 'blocked'
  );
  const stations = Object.fromEntries(STATION_ORDER.map((stationId) => {
    const saved = state.stations[stationId] as Partial<GameState['stations'][StationId]>;
    const hasManagerLevel = Number.isFinite(saved.managerLevel);
    const managerLevel = hasManagerLevel
      ? Math.max(0, Math.min(MAX_MANAGER_LEVEL, Math.floor(saved.managerLevel as number)))
      : saved.automated
        ? MAX_MANAGER_LEVEL
        : 0;
    const status = isStatus(saved.status)
      ? saved.status
      : saved.blocked
        ? 'blocked'
        : managerLevel > 0
          ? 'running'
          : 'manual';
    return [stationId, {
      level: Math.max(1, Math.floor(saved.level ?? 1)),
      capacityLevel: Math.max(0, Math.floor(saved.capacityLevel ?? 0)),
      managerLevel,
      automated: managerLevel > 0,
      boostUntil: saved.boostUntil ?? 0,
      qualityBoostUntil: saved.qualityBoostUntil ?? 0,
      produced: saved.produced ?? 0,
      status,
      statusSince: saved.statusSince ?? state.elapsedSeconds ?? 0,
      pendingStatus: isStatus(saved.pendingStatus) ? saved.pendingStatus : null,
      pendingStatusSince: saved.pendingStatusSince ?? state.elapsedSeconds ?? 0,
      blocked: status === 'blocked'
    }];
  })) as GameState['stations'];
  return {
    schemaVersion: 1,
    gameVersion: GAME_VERSION,
    region: state.region,
    owner: state.owner,
    philosophy: state.philosophy ?? 'boutique',
    cash: Magnitude.from(state.cash as unknown as string),
    renown: state.renown ?? 0,
    legacyCorks: state.legacyCorks ?? 0,
    vintage: state.vintage ?? 1,
    year: state.year ?? 2026,
    estateTier: state.estateTier ?? 1,
    elapsedSeconds: state.elapsedSeconds ?? 0,
    lastSavedAt: state.lastSavedAt ?? Date.now(),
    weather: state.weather ?? 'Golden & clear',
    weatherTemperature: state.weatherTemperature ?? (state.region === 'napa' ? 27 : 24),
    stations,
    wines: state.wines,
    inventory: state.inventory,
    legacyUpgrades: { ...emptyLegacy(), ...state.legacyUpgrades },
    bottleHistory: state.bottleHistory ?? [],
    stats: hydrateStats(state.stats),
    lifetimeStats: hydrateStats(state.lifetimeStats),
    settings: { ...DEFAULT_SETTINGS, ...state.settings },
    tutorialStep: state.tutorialStep ?? 0
  };
}
